import { CircleHelp } from "lucide-react";

/*
  Clarification Required (Design Spec 001 Phase 3).
  A read-only, derived list of evidence items marked Needs Clarification.
  Clarification is a participant-centred readiness signal — not failure,
  blame, or non-compliance. This list never changes scoring, classification,
  or source verification.
*/
export function ClarificationRequiredList({ items, framed = true }) {
  const body = (
    <div data-testid="clarification-required-list">
      <p className="px-6 pt-4 text-sm leading-relaxed text-muted-foreground">
        These items need more information, evidence, or context before a readiness judgement can
        be made about whether participant outcomes are adequately protected. Clarification is a
        readiness signal, not blame or non-compliance.
      </p>
      {(!items || items.length === 0) ? (
        <div className="px-6 py-6 text-sm text-muted-foreground" data-testid="clarification-required-empty">
          None — no items are currently awaiting clarification.
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-border border-t border-border">
          {items.map((item) => (
            <li
              key={item.id}
              data-testid={`clarification-item-${item.id}`}
              className="grid grid-cols-1 gap-2 px-6 py-4 md:grid-cols-12 md:items-start md:gap-4"
            >
              <div className="md:col-span-2">
                <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {String(item.number).padStart(2, "0")} · {item.domain}
                </span>
              </div>
              <div className="md:col-span-7">
                <p className="font-display text-[15px] font-medium leading-tight text-foreground">
                  {item.title}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{item.helper}</p>
              </div>
              <div className="flex items-center gap-2 md:col-span-3 md:justify-end">
                <span className="font-mono-label inline-flex items-center gap-1.5 rounded-sm border border-info/40 bg-info-soft px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-info">
                  <CircleHelp className="h-3.5 w-3.5" />
                  Needs Clarification
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (!framed) return <div className="overflow-hidden rounded-sm border border-border bg-card pb-1">{body}</div>;

  return (
    <section className="paper-card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border bg-card/80 px-6 py-4">
        <span className="block h-6 w-1 rounded-sm bg-info" />
        <div>
          <p className="eyebrow">§ 12 · Readiness signal</p>
          <h3 className="font-display text-lg font-medium text-foreground">Clarification Required</h3>
        </div>
        <span className="font-mono-label ml-auto text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {(items || []).length} item{(items || []).length === 1 ? "" : "s"}
        </span>
      </div>
      {body}
    </section>
  );
}
